import ValidatedForm, { FormReadiness, SubmitButton } from './forms/ValidatedForm';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LOCAL_PREVIEW } from '../localPreview';
import './css/Contact.css';
import FieldLabel, { RequiredFieldsNote, RequiredMarker } from './forms/FieldLabel';

const CONTACT_FUNCTION_URL = process.env.REACT_APP_CONTACT_FUNCTION_URL || '';
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '';
const HCAPTCHA_ONLOAD_CALLBACK = 'kenBrynHcaptchaOnLoad';

const initialValues = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    details: '',
    website: '',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneCharacterPattern = /^\+?[\d\s().-]+$/;

const getPhoneDigits = (phone) => phone.replace(/\D/g, '');

const isValidPhoneNumber = (phone) => {
    if (!phone) {
        return true;
    }

    const digits = getPhoneDigits(phone);
    const hasValidCharacters = phoneCharacterPattern.test(phone);
    const plusCount = (phone.match(/\+/g) || []).length;

    return hasValidCharacters && plusCount <= 1 && (!phone.includes('+') || phone.startsWith('+')) && digits.length >= 7 && digits.length <= 15;
};

const formatUsPhoneNumber = (phone) => {
    const hasCountryCode = phone.trim().startsWith('+1');
    const digits = getPhoneDigits(phone);
    const localDigits = hasCountryCode ? digits.slice(1, 11) : digits.slice(0, 10);

    if (!(digits.length === 10 || (hasCountryCode && digits.length === 11))) {
        return phone;
    }

    const areaCode = localDigits.slice(0, 3);
    const prefix = localDigits.slice(3, 6);
    const lineNumber = localDigits.slice(6, 10);
    const formattedLocal = `(${areaCode}) ${prefix}-${lineNumber}`;

    return hasCountryCode ? `+1 ${formattedLocal}` : formattedLocal;
};

const Contact = () => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle');
    const [message, setMessage] = useState('');
    const [verified, setVerified] = useState(false);
    const captchaRef = useRef(null);
    const captchaWidgetIdRef = useRef(null);

    useEffect(() => {
        if (!HCAPTCHA_SITE_KEY || LOCAL_PREVIEW) {
            return;
        }

        const renderCaptcha = () => {
            if (!captchaRef.current || !window.hcaptcha || captchaWidgetIdRef.current !== null) {
                return;
            }

            captchaWidgetIdRef.current = window.hcaptcha.render(captchaRef.current, {
                sitekey: HCAPTCHA_SITE_KEY,
                callback: () => setVerified(true),
                'expired-callback': () => setVerified(false),
                'error-callback': () => setVerified(false),
            });
        };

        window[HCAPTCHA_ONLOAD_CALLBACK] = renderCaptcha;

        const existingScript = document.querySelector('script[src*="hcaptcha.com/1/api.js"]');
        let listeningForLoad = false;

        if (window.hcaptcha) {
            renderCaptcha();
        } else if (existingScript) {
            if (!existingScript.src.includes('onload=')) {
                existingScript.addEventListener('load', renderCaptcha);
                listeningForLoad = true;
            }
        } else {
            const script = document.createElement('script');
            script.src = `https://js.hcaptcha.com/1/api.js?onload=${HCAPTCHA_ONLOAD_CALLBACK}&render=explicit&recaptchacompat=off`;
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }

        return () => {
            const script = document.querySelector('script[src*="hcaptcha.com/1/api.js"]');
            if (listeningForLoad) {
                script?.removeEventListener('load', renderCaptcha);
            }
            window[HCAPTCHA_ONLOAD_CALLBACK] = () => {};

            if (captchaWidgetIdRef.current !== null && window.hcaptcha) {
                window.hcaptcha.remove(captchaWidgetIdRef.current);
                captchaWidgetIdRef.current = null;
            }
        };
    }, []);

    const isConfigured = useMemo(() => Boolean(CONTACT_FUNCTION_URL && HCAPTCHA_SITE_KEY), []);

    const validate = () => {
        const nextErrors = {};
        const trimmed = {
            name: values.name.trim(),
            email: values.email.trim(),
            phone: values.phone.trim(),
            subject: values.subject.trim(),
        };

        if (!trimmed.name) {
            nextErrors.name = 'Name is required.';
        }

        if (!trimmed.email) {
            nextErrors.email = 'Email is required.';
        } else if (!emailPattern.test(trimmed.email)) {
            nextErrors.email = 'Use a valid email address.';
        }

        if (!trimmed.subject) {
            nextErrors.subject = 'Subject is required.';
        }

        if (trimmed.phone && !isValidPhoneNumber(trimmed.phone)) {
            nextErrors.phone = 'Use a valid phone number.';
        }

        if (HCAPTCHA_SITE_KEY && !window.hcaptcha?.getResponse(captchaWidgetIdRef.current)) {
            nextErrors.verification = 'Complete the human verification.';
        }

        return nextErrors;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValues((current) => ({ ...current, [name]: value }));
        setErrors((current) => ({ ...current, [name]: undefined }));
    };

    const handlePhoneChange = (event) => {
        const cleaned = event.target.value.replace(/[^\d+\s().-]/g, '').replace(/(?!^)\+/g, '');
        setValues((current) => ({ ...current, phone: cleaned }));
        setErrors((current) => ({ ...current, phone: undefined }));
    };

    const handlePhoneBlur = () => {
        setValues((current) => ({
            ...current,
            phone: formatUsPhoneNumber(current.phone.trim()),
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');

        const nextErrors = validate();
        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            setStatus('idle');
            return;
        }

        if (!isConfigured) {
            setStatus('error');
            setMessage('The contact form needs a Supabase function URL and hCaptcha site key before messages can be sent.');
            return;
        }

        if (values.website) {
            setStatus('success');
            setValues(initialValues);
            return;
        }

        setStatus('submitting');

        const payload = {
            name: values.name.trim(),
            email: values.email.trim(),
            phone: values.phone.trim(),
            subject: values.subject.trim(),
            additionalDetails: values.details.trim(),
            website: values.website,
            hcaptchaToken: window.hcaptcha.getResponse(captchaWidgetIdRef.current),
        };

        try {
            const response = await fetch(CONTACT_FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || 'Form submission failed.');
            }

            setStatus('success');
            setMessage('Thanks. We will be in touch soon.');
            setValues(initialValues);
            window.hcaptcha.reset(captchaWidgetIdRef.current);
            setVerified(false);
        } catch (error) {
            setStatus('error');
            setMessage(`Something went wrong sending your message: ${error.message}`);
            window.hcaptcha?.reset(captchaWidgetIdRef.current);
            setVerified(false);
        }
    };

    return (
        <main className="contact-page">
            {LOCAL_PREVIEW && <p className="contact-config-note">Contact delivery is disabled in the local preview. Use the lesson request form to test lessons and captured emails.</p>}
            <section className="contact-shell" aria-labelledby="contact-title">
                <div className="contact-intro">
                    <p className="contact-kicker">Contact</p>
                    <h1 id="contact-title">Get in touch with Ken & Bryn</h1>
                    <p>
                        Questions about lessons, events, bookings, or members content can be sent here.
                    </p>
                    <p>Looking for a private lesson? <Link className="contact-email-link" to="/lessons">Send a lesson request</Link> so we can keep track of your event or local preferences.</p>
                </div>

                <ValidatedForm className="contact-form" onSubmit={handleSubmit} showFeedbackOnSubmit
                    ready={isConfigured && !LOCAL_PREVIEW && verified && isValidPhoneNumber(values.phone)}
                    readinessMessage={LOCAL_PREVIEW ? 'Contact delivery is disabled in this local preview.' : !isConfigured ? 'Contact delivery is temporarily unavailable.' : !isValidPhoneNumber(values.phone) ? 'Enter a valid phone number or leave it blank.' : !verified ? 'Complete human verification to send your message.' : ''}>
                    <RequiredFieldsNote />
                    <input
                        className="contact-honeypot"
                        type="text"
                        name="website"
                        value={values.website}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                        aria-hidden="true"
                    />

                    <FieldLabel htmlFor="name" required>Name</FieldLabel>
                    <input
                        id="name"
                        name="name"
                        type="text"
                        value={values.name}
                        onChange={handleChange}
                        autoComplete="name"
                        placeholder="Jane Smith"
                        required
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                    {errors.name && <p className="contact-error" id="name-error">{errors.name}</p>}

                    <FieldLabel htmlFor="email" required>Email</FieldLabel>
                    <input
                        id="email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        autoComplete="email"
                        placeholder="jane@example.com"
                        required
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    {errors.email && <p className="contact-error" id="email-error">{errors.email}</p>}

                    <label htmlFor="phone">Phone number</label>
                    <input
                        id="phone"
                        name="phone"
                        type="text"
                        value={values.phone}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        autoComplete="tel"
                        inputMode="tel"
                        placeholder="+1 (555) 123-4567"
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={errors.phone ? 'phone-error' : undefined}
                    />
                    {errors.phone && <p className="contact-error" id="phone-error">{errors.phone}</p>}

                    <FieldLabel htmlFor="subject" required>Subject</FieldLabel>
                    <input
                        id="subject"
                        name="subject"
                        type="text"
                        value={values.subject}
                        onChange={handleChange}
                        placeholder="Booking inquiry"
                        required
                        aria-invalid={Boolean(errors.subject)}
                        aria-describedby={errors.subject ? 'subject-error' : undefined}
                    />
                    {errors.subject && <p className="contact-error" id="subject-error">{errors.subject}</p>}

                    <label htmlFor="details">Additional Details</label>
                    <textarea
                        id="details"
                        name="details"
                        rows="6"
                        value={values.details}
                        onChange={handleChange}
                        placeholder="Share dates, location, event details, or anything else we should know."
                    />

                    {!LOCAL_PREVIEW && <div className="contact-verification">
                        {HCAPTCHA_SITE_KEY && !LOCAL_PREVIEW && <p>Human verification<RequiredMarker /></p>}
                        {HCAPTCHA_SITE_KEY ? (
                            <div ref={captchaRef}></div>
                        ) : (
                            <p className="contact-config-note">Add an hCaptcha site key to enable human verification.</p>
                        )}
                    </div>}
                    {errors.verification && <p className="contact-error">{errors.verification}</p>}

                    <FormReadiness /><SubmitButton type="submit" disabled={status === 'submitting'}>
                        {status === 'submitting' ? 'Sending...' : 'Send Message'}
                    </SubmitButton>

                    {message && (
                        <p className={`contact-status contact-status-${status}`} role="status" aria-live="polite">
                            {message}
                        </p>
                    )}
                </ValidatedForm>
            </section>
        </main>
    );
};

export default Contact;
